import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Check } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { subDays } from "date-fns";
import { formatMoney, startOfDayISO } from "@/lib/format";
import { useActiveOrg } from "@/features/tenancy/hooks";
import { Panel } from "@/components/ui/Panel";
import { StatusPill } from "@/components/ui/StatusPill";
import { orderStatus } from "@/features/orders/status";
import { SalesChart } from "./SalesChart";
import {
  fetchMasVendidos,
  fetchUltimasVentas,
  fetchVentasPorDia,
  type ProductoVendido,
  type VentaReciente,
} from "./api";
import { Skeleton } from "@/components/ui/states";
import { cn } from "@/lib/cn";

interface AvisoDatos {
  k: string;
  n: number;
  texto: string;
  to: string;
  tono: "info" | "warn" | "crit";
}

interface Summary {
  salesToday: number;
  ordersToday: number;
  salesYesterday: number;
  ordersYesterday: number;
  newOrders: number;
  toVerify: number;
  outOfStock: number;
  lowStock: number;
}

/**
 * Lo que hay que atender ahora, no lo que paso este trimestre.
 *
 * Quien abre esto atiende una tienda entre cliente y cliente. La comparacion
 * con ayer entra porque una cifra sola no dice nada: "S/ 240" no es bueno ni
 * malo hasta que se sabe que ayer a esta hora iban 180.
 *
 * Se compara contra la MISMA hora de ayer y no contra el dia entero. A las
 * diez de la mañana, "vas un 60% por debajo de ayer" seria mentira: ayer
 * tambien iba por ahi a las diez.
 */
async function fetchSummary(organizationId: string, timeZone: string): Promise<Summary> {
  const inicioHoy = new Date(startOfDayISO(timeZone));
  const inicioAyer = subDays(inicioHoy, 1);
  const mismaHoraAyer = subDays(new Date(), 1);

  const [hoy, ayer, pendientes, stock] = await Promise.all([
    supabase
      .from("orders")
      .select("total")
      .eq("organization_id", organizationId)
      .neq("order_status", "cancelled")
      .gte("created_at", inicioHoy.toISOString()),
    supabase
      .from("orders")
      .select("total")
      .eq("organization_id", organizationId)
      .neq("order_status", "cancelled")
      .gte("created_at", inicioAyer.toISOString())
      .lt("created_at", mismaHoraAyer.toISOString()),
    supabase
      .from("orders")
      .select("order_status, payment_status")
      .eq("organization_id", organizationId)
      .neq("order_status", "cancelled"),
    supabase
      .from("inventory_status")
      .select("stock_status")
      .eq("organization_id", organizationId),
  ]);

  for (const r of [hoy, ayer, pendientes, stock]) {
    if (r.error) throw r.error;
  }

  // Se suma para mostrar, no para decidir. Los totales que importan los
  // calcula PostgreSQL en cada pedido.
  const sumar = (filas: { total: unknown }[] | null) =>
    (filas ?? []).reduce((s, o) => s + Number(o.total), 0);

  return {
    salesToday: sumar(hoy.data),
    ordersToday: (hoy.data ?? []).length,
    salesYesterday: sumar(ayer.data),
    ordersYesterday: (ayer.data ?? []).length,
    newOrders: (pendientes.data ?? []).filter((o) => o.order_status === "new").length,
    toVerify: (pendientes.data ?? []).filter((o) => o.payment_status === "pending_verification").length,
    outOfStock: (stock.data ?? []).filter((s) => s.stock_status === "out_of_stock").length,
    lowStock: (stock.data ?? []).filter((s) => s.stock_status === "low").length,
  };
}

export function DashboardPage() {
  const { org } = useActiveOrg();
  const tz = org?.timezone ?? "America/Lima";
  const currency = org?.currency ?? "PEN";

  const query = useQuery({
    queryKey: ["dashboard", org?.organizationId],
    queryFn: () => fetchSummary(org!.organizationId, tz),
    enabled: Boolean(org),
  });

  const serie = useQuery({
    queryKey: ["dashboard-serie", org?.organizationId],
    queryFn: () => fetchVentasPorDia(org!.organizationId, tz),
    enabled: Boolean(org),
  });

  const vendidos = useQuery({
    queryKey: ["dashboard-vendidos", org?.organizationId],
    queryFn: () => fetchMasVendidos(org!.organizationId),
    enabled: Boolean(org),
  });

  const recientes = useQuery({
    queryKey: ["dashboard-recientes", org?.organizationId],
    queryFn: () => fetchUltimasVentas(org!.organizationId),
    enabled: Boolean(org),
  });

  const d = query.data;

  // Los avisos solo existen cuando hay algo que atender. Cuatro ceros en una
  // rejilla es lo mas desmoralizante que puede ver una tienda que empieza, y
  // ademas entrena a no mirar.
  const avisos: AvisoDatos[] = [];
  if (d) {
    const plural = (n: number, uno: string, varios: string) => (n === 1 ? uno : varios);

    if (d.newOrders > 0) {
      avisos.push({
        k: "nuevos",
        n: d.newOrders,
        texto: plural(d.newOrders, "pedido por confirmar", "pedidos por confirmar"),
        to: "/ventas",
        tono: "info",
      });
    }
    if (d.toVerify > 0) {
      avisos.push({
        k: "verificar",
        n: d.toVerify,
        texto: plural(d.toVerify, "pago por verificar", "pagos por verificar"),
        to: "/ventas",
        tono: "warn",
      });
    }
    if (d.outOfStock > 0) {
      avisos.push({
        k: "agotados",
        n: d.outOfStock,
        texto: plural(d.outOfStock, "producto agotado", "productos agotados"),
        to: "/inventario",
        tono: "crit",
      });
    }
    if (d.lowStock > 0) {
      avisos.push({
        k: "bajo",
        n: d.lowStock,
        texto: plural(d.lowStock, "producto por reponer", "productos por reponer"),
        to: "/inventario",
        tono: "warn",
      });
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-head font-semibold">{org?.storeName ?? org?.name ?? "Panel"}</h1>

      <LecturaDelDia datos={d} currency={currency} />

      <section className="flex flex-col gap-2">
        <h2 className="text-mark font-mono uppercase text-ink-3">Requiere atención</h2>

        {!d ? (
          <Skeleton className="h-14 w-full rounded-lg" />
        ) : avisos.length === 0 ? (
          <p className="flex items-center gap-2 rounded-lg border border-line bg-surface px-4 py-3.5 text-ui text-ink-2">
            <Check className="size-4 shrink-0 text-ok" />
            Nada pendiente. Todo lo que entró está atendido.
          </p>
        ) : (
          <div className="flex flex-col gap-1.5">
            {avisos.map((a) => (
              <Aviso key={a.k} {...a} />
            ))}
          </div>
        )}
      </section>

      {serie.data && (
        <SalesChart dias={serie.data} currency={currency} timeZone={tz} />
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        <MasVendidos datos={vendidos.data} currency={currency} />
        <UltimasVentas datos={recientes.data} currency={currency} />
      </div>
    </div>
  );
}

/**
 * Lo mas vendido del mes.
 *
 * La barra de fondo compara cada linea con la primera. Es lo que convierte
 * una lista de numeros en algo que se lee de un vistazo: se ve enseguida si
 * el primero vende el doble que el segundo o solo un poco mas.
 */
function MasVendidos({ datos, currency }: { datos?: ProductoVendido[]; currency: string }) {
  return (
    <Panel title="Más vendido · 30 días">
      {!datos ? (
        <div className="flex flex-col gap-2">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-5 w-full" />)}
        </div>
      ) : datos.length === 0 ? (
        <p className="py-4 text-center text-ui text-ink-2">
          Cuando vendas algo, aquí verás qué se mueve.
        </p>
      ) : (
        <ol className="flex flex-col gap-2">
          {datos.map((p, i) => (
            <li key={`${p.nombre}-${p.variante ?? ""}`} className="relative">
              <span
                aria-hidden
                style={{ width: `${(p.unidades / datos[0]!.unidades) * 100}%` }}
                className="absolute inset-y-0 left-0 rounded-sm bg-accent-soft"
              />
              <span className="relative flex items-center gap-2.5 px-2 py-1.5">
                <span className="tnum font-mono text-mark text-ink-3">{i + 1}</span>
                <span className="min-w-0 flex-1 truncate text-ui">
                  {p.nombre}
                  {p.variante && <span className="text-ink-3"> · {p.variante}</span>}
                </span>
                <span className="tnum shrink-0 font-mono text-meta text-ink-2">
                  {p.unidades} u · {formatMoney(p.importe, currency)}
                </span>
              </span>
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
}

function UltimasVentas({ datos, currency }: { datos?: VentaReciente[]; currency: string }) {
  return (
    <Panel title="Últimos pedidos">
      {!datos ? (
        <div className="flex flex-col gap-2">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-5 w-full" />)}
        </div>
      ) : datos.length === 0 ? (
        <p className="py-4 text-center text-ui text-ink-2">
          Todavía no entra ningún pedido.
        </p>
      ) : (
        <ul className="flex flex-col">
          {datos.map((v) => {
            const est = orderStatus(v.estado, v.entrega);
            return (
              <li key={v.id}>
                <Link
                  to={`/ventas/${v.id}`}
                  className="flex items-center gap-2.5 rounded-md px-2 py-2 hover:bg-sunk"
                >
                  <span className="tnum font-mono text-meta">{v.numero}</span>
                  <span className="min-w-0 flex-1 truncate text-ui text-ink-2">
                    {v.cliente ?? "Sin nombre"}
                  </span>
                  <StatusPill tone={est.tone}>{est.label}</StatusPill>
                  <span className="tnum shrink-0 font-mono text-meta font-medium">
                    {formatMoney(v.total, currency)}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

/**
 * La lectura del dia.
 *
 * Es lo unico grande de la pantalla y esta en la tipografia monoespaciada a
 * proposito: el panel se lee como un instrumento, no como un informe. La
 * cifra manda, la comparacion la explica y el numero de pedidos la sostiene.
 */
function LecturaDelDia({ datos, currency }: { datos?: Summary; currency: string }) {
  if (!datos) {
    return (
      <div className="rounded-lg border border-line bg-surface p-5">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="mt-3 h-10 w-48" />
      </div>
    );
  }

  const { salesToday, ordersToday, salesYesterday } = datos;

  // Sin referencia no se inventa una: el primer dia de una tienda no tiene
  // con que compararse, y un "+100%" ahi seria ruido con aire de dato.
  const hayReferencia = salesYesterday > 0;
  const variacion = hayReferencia
    ? Math.round(((salesToday - salesYesterday) / salesYesterday) * 100)
    : null;

  return (
    <Link
      to="/ventas"
      className="group flex flex-col gap-3 rounded-lg border border-line bg-surface p-5 transition-colors hover:border-ink-3 sm:flex-row sm:items-end sm:justify-between"
    >
      <div>
        <span className="text-mark block font-mono uppercase text-ink-3">Ventas de hoy</span>

        <span className="tnum mt-1.5 block font-mono text-figure font-semibold">
          {formatMoney(salesToday, currency)}
        </span>

        <span className="mt-2 block text-meta text-ink-2">
          {ordersToday === 0
            ? "Sin pedidos todavía"
            : `${ordersToday} ${ordersToday === 1 ? "pedido" : "pedidos"}`}
          {variacion !== null && (
            <>
              {" · "}
              <span
                className={cn(
                  "tnum font-medium",
                  variacion > 0 ? "text-ok" : variacion < 0 ? "text-crit" : "text-ink-2",
                )}
              >
                {variacion > 0 ? "+" : ""}
                {variacion}%
              </span>{" "}
              que ayer a esta hora
            </>
          )}
        </span>
      </div>

      <span className="flex items-center gap-1.5 text-meta text-ink-3 transition-colors group-hover:text-ink-2">
        Ver ventas
        <ArrowRight className="size-3.5" />
      </span>
    </Link>
  );
}

/**
 * Un aviso es una frase con una cifra dentro, no una tarjeta con un numero.
 * Se lee de corrido —"3 pagos por verificar"— y lleva a la lista ya filtrada:
 * un numero que no lleva a ninguna parte es adorno.
 */
function Aviso({
  n,
  texto,
  to,
  tono,
}: {
  n: number;
  texto: string;
  to: string;
  tono: "info" | "warn" | "crit";
}) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-3 rounded-lg border border-line bg-surface px-4 py-3 transition-colors hover:border-ink-3"
    >
      <span
        className={cn(
          "h-7 w-1 shrink-0 rounded-full",
          tono === "info" && "bg-info",
          tono === "warn" && "bg-warn",
          tono === "crit" && "bg-crit",
        )}
        aria-hidden
      />
      <span className="tnum font-mono text-emph font-semibold">{n}</span>
      <span className="text-ui text-ink-2">{texto}</span>
      <ArrowRight className="ml-auto size-3.5 shrink-0 text-ink-3 transition-colors group-hover:text-ink-2" />
    </Link>
  );
}
